type BridgeAuthResult = {
  access_token: string;
  refresh_token: string;
  user?: {
    id: string;
    email: string;
    full_name: string;
    avatar_url?: string | null;
  };
};

type BridgeAuthError = {
  message: string;
};

type BridgeAuthPayload = BridgeAuthResult | BridgeAuthError;

type FlutterBridgeShape = {
  requestGoogleLogin: () => void;
  // Only the iOS native shell defines this. Its absence is how the web
  // layer decides whether to show the "Sign in with Apple" button.
  requestAppleLogin?: () => void;
  logout?: () => void;
};

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    JapanCargoBridge?: FlutterBridgeShape;
    onJapanCargoAuth?: (payload: string | BridgeAuthPayload) => void;
  }
}

export const isInFlutterWebView = (): boolean =>
  typeof window !== 'undefined' && typeof window.JapanCargoBridge !== 'undefined';

export const requestFlutterGoogleLogin = (): boolean => {
  if (!isInFlutterWebView() || !window.JapanCargoBridge) return false;
  window.JapanCargoBridge.requestGoogleLogin();
  return true;
};

// Feature-detect the native Apple login capability. Only the iOS native
// shell exposes `requestAppleLogin`; on Android/desktop browsers this is
// `undefined`, so the Apple button stays hidden for normal web users.
export const isAppleBridgeLoginAvailable = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.JapanCargoBridge?.requestAppleLogin === 'function';

export const requestFlutterAppleLogin = (): boolean => {
  if (typeof window.JapanCargoBridge?.requestAppleLogin !== 'function') return false;
  window.JapanCargoBridge.requestAppleLogin();
  return true;
};

export const requestFlutterLogout = (): void => {
  window.JapanCargoBridge?.logout?.();
};

const isAuthSuccess = (payload: unknown): payload is BridgeAuthResult =>
  typeof payload === 'object' &&
  payload !== null &&
  typeof (payload as BridgeAuthResult).access_token === 'string' &&
  typeof (payload as BridgeAuthResult).refresh_token === 'string';

const isAuthError = (payload: unknown): payload is BridgeAuthError =>
  typeof payload === 'object' &&
  payload !== null &&
  typeof (payload as BridgeAuthError).message === 'string';

const parsePayload = (payload: string | BridgeAuthPayload): BridgeAuthPayload | null => {
  try {
    return typeof payload === 'string'
      ? (JSON.parse(payload) as BridgeAuthPayload)
      : payload;
  } catch (err) {
    console.error('[bridge] Failed to parse auth payload', err);
    return null;
  }
};

type FlutterAuthListener = {
  onSuccess: (result: BridgeAuthResult) => void;
  onError?: (message: string) => void;
};

// Module-level subscriber list so multiple parts of the app (e.g. a
// page-level handler and a global fallback) can all observe Flutter's
// auth callback. Without this, the last `subscribeToFlutterAuth` call
// would overwrite the previous handler.
const listeners = new Set<FlutterAuthListener>();

const dispatch = (payload: string | BridgeAuthPayload): void => {
  const parsed = parsePayload(payload);
  if (parsed === null) return;
  if (isAuthSuccess(parsed)) {
    listeners.forEach((l) => l.onSuccess(parsed));
    return;
  }
  if (isAuthError(parsed)) {
    listeners.forEach((l) => l.onError?.(parsed.message));
    return;
  }
  console.warn('[bridge] Auth payload had no usable shape', parsed);
};

const ensureGlobalHandler = (): void => {
  if (typeof window === 'undefined') return;
  // Always (re)install: HMR or repeated installs are safe because we
  // route through the shared listener set.
  window.onJapanCargoAuth = dispatch;
};

ensureGlobalHandler();

export const subscribeToFlutterAuth = (
  onSuccess: (result: BridgeAuthResult) => void,
  onError?: (message: string) => void,
): (() => void) => {
  ensureGlobalHandler();
  const entry: FlutterAuthListener = { onSuccess, onError };
  listeners.add(entry);
  return () => {
    listeners.delete(entry);
  };
};

export type { BridgeAuthResult, BridgeAuthError, BridgeAuthPayload };
